import React, { forwardRef } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Typography,
  Space,
  Statistic,
  Row,
  Col,
  Divider,
} from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  AimOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  colorStatusWarning,
  formatDateTime,
  statusTextWarning,
} from "../../utils/constants";
import { IMAGE_URL } from "../../constants";
import { countries } from "../../utils/countries";

const { Title, Text } = Typography;

const WarningScreenshotCard = forwardRef(({ warning }, ref) => {
  const renderStatusInfo = () => {
    if (warning.status === "finalized" && warning?.infraction?.user) {
      return (
        <Card size="small" className="mb-6 border-green-500">
          <div className="flex items-center">
            <CheckCircleOutlined className="text-green-500 text-xl mr-2" />
            <div>
              <Text strong>Finalisée par :</Text>
              <div className="flex items-center mt-1">
                <UserOutlined className="text-green-500 mr-2" />
                <Text>{warning.infraction.user.full_name}</Text>
                <Text type="secondary" className="ml-2">
                  {formatDateTime(warning.infraction.datetime_infraction)}
                </Text>
              </div>
              <Text type="secondary" className="mt-1">
                Cet avertissement a été converti en infraction.
              </Text>
            </div>
          </div>
        </Card>
      );
    }

    if (warning.status === "canceled" && warning.cancel) {
      return (
        <Card size="small" className="mb-6 border-red-500">
          <div className="flex items-center">
            <StopOutlined className="text-red-500 text-xl mr-2" />
            <div>
              <Text strong>Annulée par:</Text>
              <div className="flex items-center mt-1">
                <UserOutlined className="text-red-500 mr-2" />
                <Text>{warning.cancel.full_name}</Text>
                <Text type="secondary" className="ml-2">
                  {formatDateTime(warning.cancelled_at)}
                </Text>
              </div>
              {warning.cancellation_reason && (
                <div className="mt-2">
                  <Text strong>Motif:</Text>
                  <Text className="ml-2">
                    {warning.cancellation_reason.name}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </Card>
      );
    }

    return null;
  };

  return (
    <div
      ref={ref}
      style={{ width: "900px", padding: "32px", background: "#faf5ff" }}
    >
      <Card className="shadow-lg border-t-4 border-purple-600">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Title level={2} className="text-purple-700 m-0">
              Détails de l'Avertissement #{warning.reference}
            </Title>
            <Tag
              color={colorStatusWarning[warning.status]}
              className="text-lg px-4 py-1"
            >
              {statusTextWarning(warning.status)}
            </Tag>
          </div>
        </div>

        {renderStatusInfo()}

        <Row gutter={24}>
          <Col span={14}>
            <Statistic
              title="Type d'Infraction"
              value={warning.type_infraction?.name}
              valueStyle={{ color: "#722ed1" }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Col>
        </Row>

        <Divider className="my-6" />

        <Descriptions column={1} className="mb-6">
          <Descriptions.Item
            label={
              <Text strong className="text-purple-700">
                Description
              </Text>
            }
          >
            {warning.description}
          </Descriptions.Item>
        </Descriptions>

        <Row gutter={24} className="mb-6">
          <Col span={12}>
            <Card
              size="small"
              title={
                <Text strong className="text-purple-700">
                  Informations du Véhicule
                </Text>
              }
              className="h-full"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Matricule">
                  {warning.matricule}
                  {warning?.vehicle?.country &&
                    ` ( ${
                      countries.find(
                        (c) => c.code === warning.vehicle.country
                      )?.name || ""
                    } )`}
                </Descriptions.Item>
                <Descriptions.Item label="Marque">
                  {warning.brand}
                </Descriptions.Item>
                <Descriptions.Item label="Modèle">
                  {warning.model}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col span={12}>
            <Card
              size="small"
              title={
                <Text strong className="text-purple-700">
                  Détails de l'Avertissement
                </Text>
              }
              className="h-full"
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <ClockCircleOutlined className="mr-2" />
                      <span>Date et Heure</span>
                    </div>
                  }
                >
                  {formatDateTime(warning.datetime_warning)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Contrat</span>
                    </div>
                  }
                >
                  {warning?.zone?.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Zone</span>
                    </div>
                  }
                >
                  {warning?.section?.name || "Non défini"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <div className="flex items-center">
                      <AimOutlined className="mr-2" />
                      <span>Rue</span>
                    </div>
                  }
                >
                  {warning?.street?.name || "Non défini"}
                  {" - "}
                  {warning?.street?.name_ar || "غير محدد"}
                </Descriptions.Item>
                <Descriptions.Item label="Coordonnées">
                  Lat: {warning.latitude}, Long: {warning.longitude}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

        <Card
          size="small"
          title={
            <Text strong className="text-purple-700">
              Preuves
            </Text>
          }
          className="mb-6"
        >
          <Space direction="vertical" size="middle" className="w-full">
            {warning.photos && (
              <div>
                <Text strong className="mr-3">
                  Photos:
                </Text>
                <Space size={[8, 8]} wrap>
                  {warning.photos.split(",").map((photo, index) => (
                    <img
                      key={index}
                      width={150}
                      height={150}
                      src={IMAGE_URL + photo}
                      alt={`Photo d'avertissement ${index + 1}`}
                      className="object-cover rounded"
                      crossOrigin="anonymous"
                    />
                  ))}
                </Space>
              </div>
            )}
            <div className="flex justify-between items-center">
              <div></div>
              <div>
                <Text strong>Agent Verbalisateur:</Text>
                <div className="flex items-center mt-2">
                  <UserOutlined className="text-purple-600 text-xl mr-2" />
                  <Text>{warning?.user?.full_name}</Text>
                </div>
              </div>
            </div>
          </Space>
        </Card>
      </Card>
    </div>
  );
});

WarningScreenshotCard.displayName = "WarningScreenshotCard";

export default WarningScreenshotCard;
